import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { CARD_PO_PICKER_PREFIX } from '../../utils/cardExpensePurchaseMatch';

export interface QBDealOption {
  id: string;
  label: string;
  /** Lowercase haystack for filtering (project #, name, client, etc.). */
  searchText?: string;
}

interface QBDealPickerProps {
  value: string;
  onChange: (dealId: string) => void;
  dealOptions: QBDealOption[];
  emptyLabel: string;
  unmatched?: boolean;
  disabled?: boolean;
  /** Extra choices (e.g. company overhead) rendered above the project list. */
  specialOptions?: QBDealOption[];
  /** Deal ids to pin at the top when the search box is empty. */
  suggestedIds?: string[];
  'aria-label'?: string;
}

const MENU_MAX_HEIGHT = 280;
const MENU_MIN_WIDTH = 280;
/** Prefer a wider menu so long Project/PO labels (esp. PO titles) stay readable. */
const MENU_PREFERRED_WIDTH = 420;

function matchesQuery(option: QBDealOption, q: string): boolean {
  if (!q) return true;
  const haystack = option.searchText || option.label.toLowerCase();
  return haystack.includes(q);
}

function menuPosition(rect: DOMRect): React.CSSProperties {
  const pad = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(
    Math.max(rect.width, MENU_MIN_WIDTH, MENU_PREFERRED_WIDTH),
    Math.max(MENU_MIN_WIDTH, vw - pad * 2),
  );
  let left = rect.left;
  if (left + width + pad > vw) {
    left = Math.max(pad, vw - width - pad);
  }

  const spaceBelow = vh - rect.bottom - pad;
  const spaceAbove = rect.top - pad;
  const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;

  if (openUp) {
    const maxHeight = Math.min(MENU_MAX_HEIGHT, Math.max(120, spaceAbove - 4));
    return {
      position: 'fixed',
      left,
      width,
      bottom: vh - rect.top + 4,
      maxHeight,
    };
  }

  return {
    position: 'fixed',
    left,
    width,
    top: rect.bottom + 4,
    maxHeight: Math.min(MENU_MAX_HEIGHT, Math.max(120, spaceBelow - 4)),
  };
}

const QBDealPicker: React.FC<QBDealPickerProps> = ({
  value,
  onChange,
  dealOptions,
  emptyLabel,
  unmatched = false,
  disabled = false,
  specialOptions = [],
  suggestedIds = [],
  'aria-label': ariaLabel = 'Linked deal',
}) => {
  const listboxId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () =>
      dealOptions.find(d => d.id === value)
      || specialOptions.find(d => d.id === value),
    [dealOptions, specialOptions, value],
  );

  const triggerLabel = selected?.label || emptyLabel;
  const showUnmatchedStyle = unmatched && !value;

  const q = filterText.trim().toLowerCase();
  const filteredDeals = useMemo(() => {
    const matching = dealOptions.filter(d => matchesQuery(d, q));
    if (!q && suggestedIds.length > 0) {
      const suggested = suggestedIds
        .map(id => matching.find(d => d.id === id))
        .filter((d): d is QBDealOption => Boolean(d));
      const suggestedSet = new Set(suggested.map(d => d.id));
      const rest = matching.filter(d => !suggestedSet.has(d.id));
      return [...suggested, ...rest];
    }
    if (q && suggestedIds.length > 0) {
      return matching.sort((a, b) => {
        const ai = suggestedIds.indexOf(a.id);
        const bi = suggestedIds.indexOf(b.id);
        if (ai === -1 && bi === -1) return a.label.localeCompare(b.label);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
    }
    return matching;
  }, [dealOptions, q, suggestedIds]);

  const filteredSpecial = useMemo(
    () => specialOptions.filter(d => matchesQuery(d, q)),
    [specialOptions, q],
  );

  const suggestedPoIds = useMemo(
    () => suggestedIds.filter(id => id.startsWith(CARD_PO_PICKER_PREFIX)),
    [suggestedIds],
  );
  const suggestedDealIds = useMemo(
    () => suggestedIds.filter(id => !id.startsWith(CARD_PO_PICKER_PREFIX)),
    [suggestedIds],
  );

  const showSuggestedPoHeader =
    !q && suggestedPoIds.length > 0 && filteredDeals.some(d => suggestedPoIds.includes(d.id));
  const showSuggestedHeader =
    !q && suggestedDealIds.length > 0 && filteredDeals.some(d => suggestedDealIds.includes(d.id));

  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    setMenuStyle(menuPosition(el.getBoundingClientRect()));
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setFilterText('');
  }, []);

  const open = useCallback(() => {
    if (disabled) return;
    reposition();
    setIsOpen(true);
  }, [disabled, reposition]);

  const selectDeal = useCallback(
    (dealId: string) => {
      onChange(dealId);
      close();
      triggerRef.current?.focus();
    },
    [onChange, close],
  );

  useEffect(() => {
    if (!isOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target)) return;
      const menu = document.getElementById(listboxId);
      if (menu?.contains(target)) return;
      close();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isOpen, close, listboxId]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  useEffect(() => {
    if (!isOpen) return;
    const onReflow = () => reposition();
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [isOpen, reposition]);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [isOpen]);

  const menu = isOpen
    ? createPortal(
        <div
          id={listboxId}
          className="qbtx-deal-picker-menu"
          style={menuStyle}
          role="listbox"
          aria-label={ariaLabel}
        >
          <div className="qbtx-deal-picker-search-wrap">
            <Search size={13} aria-hidden />
            <input
              ref={searchRef}
              type="search"
              className="qbtx-deal-picker-search"
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              placeholder="Search project #, name, client, or PO…"
              aria-label="Search projects or purchase orders"
              onKeyDown={e => {
                if (e.key === 'Enter' && !q) {
                  e.preventDefault();
                  selectDeal('');
                }
              }}
            />
          </div>
          <div className="qbtx-deal-picker-options">
            <button
              type="button"
              role="option"
              aria-selected={!value}
              className={`qbtx-deal-picker-option ${!value ? 'selected' : ''} empty-option`}
              title={emptyLabel}
              onClick={() => selectDeal('')}
            >
              {emptyLabel}
            </button>
            {filteredSpecial.map(d => (
              <button
                key={d.id}
                type="button"
                role="option"
                aria-selected={d.id === value}
                className={`qbtx-deal-picker-option special-option ${d.id === value ? 'selected' : ''}`}
                title={d.label}
                onClick={() => selectDeal(d.id)}
              >
                {d.label}
              </button>
            ))}
            {filteredDeals.length > 0 ? (
              filteredDeals.map((d, index) => {
                const isPoSuggested = !q && suggestedPoIds.includes(d.id);
                const isDealSuggested = !q && suggestedDealIds.includes(d.id);
                const prev = index > 0 ? filteredDeals[index - 1] : null;
                const prevPo = prev ? !q && suggestedPoIds.includes(prev.id) : false;
                const prevDeal = prev ? !q && suggestedDealIds.includes(prev.id) : false;
                const showPoHead = isPoSuggested && !prevPo;
                const showDealHead = isDealSuggested && !prevDeal && !isPoSuggested;
                return (
                  <React.Fragment key={d.id}>
                    {showPoHead && showSuggestedPoHeader && (
                      <div className="qbtx-deal-picker-section-label">Suggested PO</div>
                    )}
                    {showDealHead && showSuggestedHeader && (
                      <div className="qbtx-deal-picker-section-label">Suggested</div>
                    )}
                    <button
                      type="button"
                      role="option"
                      aria-selected={d.id === value}
                      className={`qbtx-deal-picker-option ${d.id === value ? 'selected' : ''} ${isPoSuggested || isDealSuggested ? 'suggested-option' : ''}`}
                      title={d.label}
                      onClick={() => selectDeal(d.id)}
                    >
                      {d.label}
                    </button>
                  </React.Fragment>
                );
              })
            ) : filteredSpecial.length === 0 ? (
              <div className="qbtx-deal-picker-empty">No projects or POs match &ldquo;{filterText}&rdquo;</div>
            ) : null}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="qbtx-deal-picker-wrap" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`qbtx-deal-picker ${showUnmatchedStyle ? 'unmatched' : ''}`}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={selected ? triggerLabel : undefined}
        onClick={() => (isOpen ? close() : open())}
      >
        <span className="qbtx-deal-picker-label">{triggerLabel}</span>
        <ChevronDown size={14} className={`qbtx-deal-picker-chevron ${isOpen ? 'open' : ''}`} aria-hidden />
      </button>
      {menu}
    </div>
  );
};

export default QBDealPicker;
